import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';

// ============================================================
// TYPES
// ============================================================

interface FactorDefinition {
  name: string;
  displayName: string;
  hitEvidence: string;
  missEvidence: string;
  disabled?: boolean;
  disabledReason?: string;
}

interface DataSourceGroup {
  id: string;
  name: string;
  description: string;
  factorCount: number;
  factors: FactorDefinition[];
}

// ============================================================
// AGENT DISPLAY NAMES
// ============================================================

const AGENT_DISPLAY: Record<string, string> = {
  coo_scoring: 'COO Scoring Agent',
  ndc_lookup: 'NDC Lookup Agent',
  qrs: 'Quality Rating System',
  pis: 'Pricing Intelligence System',
  coors: 'COORS Agent',
  pns: 'Political Nexus Scoring',
};

// ============================================================
// FACTOR DISPLAY NAME HELPER
// ============================================================

// Disabled factors — the COO Scoring Agent has evaluated all 39 factors and
// determined that these 10 are existence/status checks that cannot confirm
// or validate a specific country of origin.  They are still evaluated and
// displayed (grayed out) but do NOT contribute to the COO confidence score.
// When a data source returns no data for a product, factors evaluate as MISS
// (not disabled) — the factor itself is still valuable for scoring.
const COO_DISABLED_FACTORS: Record<string, string> = {
  product_found_in_ndc_directory: 'Being listed in the FDA NDC directory confirms FDA registration but provides no evidence about manufacturing country of origin.',
  product_marketing_status_active: 'Active marketing status confirms the product is legally marketed but says nothing about where it is manufactured.',
  manufacturer_labeler_identified: 'Identifying the labeler confirms who markets the product but does not confirm the country where the product is manufactured.',
  import_refusal_found_for_product: 'The presence/absence of import refusals indicates border activity but cannot confirm a specific country of origin.',
  import_refusal_found_for_labeler: 'The presence/absence of import refusals for the labeler cannot confirm a specific country of origin.',
  shortage_record_found: 'A drug shortage record indicates supply disruption but does not confirm manufacturing location.',
  recall_records_found: 'Recall records indicate quality issues but do not confirm manufacturing location.',
  package_image_found: 'Finding a package image confirms packaging data is available but provides no evidence about COO.',
  package_text_extracted: 'Extracting text from a package image is a data-processing step that does not itself confirm COO.',
  sec_filing_found: 'The existence of an SEC filing confirms the company is publicly traded but does not confirm manufacturing location.',
};

// Disabled factors for QRS — the 5 FDA Inspections/483s factors are disabled
// because the data source is not yet connected to a live API.
const QRS_DISABLED_FACTORS: Record<string, string> = {
  form_483_issued: 'FDA Inspections/483s data source is not yet connected to a live API. This factor cannot be meaningfully evaluated until facility inspection data is available.',
  warning_letter_issued: 'FDA Inspections/483s data source is not yet connected to a live API. Warning letter data requires access to the FDA compliance database which is not yet integrated.',
  inspection_within_2_years: 'FDA Inspections/483s data source is not yet connected to a live API. Inspection recency cannot be determined without live inspection records.',
  multiple_observations: 'FDA Inspections/483s data source is not yet connected to a live API. Observation counts require parsed 483 form data which is not yet available.',
  cgmp_violation_found: 'FDA Inspections/483s data source is not yet connected to a live API. cGMP violation detection requires detailed inspection observation data.',
};

function formatFactorName(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bCoo\b/g, 'COO')
    .replace(/\bNdc\b/g, 'NDC')
    .replace(/\bFdf\b/g, 'FDF')
    .replace(/\bSpl\b/g, 'SPL')
    .replace(/\bTri\b/g, 'TRI')
    .replace(/\bSec\b/g, 'SEC')
    .replace(/\bOcr\b/g, 'OCR')
    .replace(/\bNaics\b/g, 'NAICS')
    .replace(/\bEpa\b/g, 'EPA')
    .replace(/\bFda\b/g, 'FDA')
    .replace(/\bFaers\b/g, 'FAERS')
    .replace(/\bCgmp\b/g, 'cGMP');
}

// ============================================================
// COO: ALL 39 FACTOR DEFINITIONS (9 Data Sources)
// ============================================================

const COO_DATA_SOURCES: DataSourceGroup[] = [
  {
    id: 'openfda_ndc',
    name: 'OpenFDA NDC Directory',
    description: 'The FDA National Drug Code (NDC) Directory contains information about all drugs registered for commercial distribution in the United States. This source verifies whether the product exists in the US regulatory system.',
    factorCount: 4,
    factors: [
      {
        name: 'product_found_in_ndc_directory',
        displayName: 'Product Found in NDC Directory',
        hitEvidence: 'Product NDC {ndc} is registered in the FDA National Drug Code directory, confirming it is a legitimate US-regulated drug product. This supports COO verification because only products with valid US distribution chains receive NDC assignments.',
        missEvidence: 'Product was not found in the FDA NDC directory. Without NDC registration, there is no FDA confirmation that this product has an active US distribution chain.',
      },
      {
        name: 'manufacturer_labeler_identified',
        displayName: 'Manufacturer / Labeler Identified',
        hitEvidence: 'The FDA-registered labeler is \'{labeler}\'. Identifying the labeler is critical for COO determination because the labeler\'s registered country and operations indicate where the product enters the US supply chain.',
        missEvidence: 'No labeler could be identified from FDA NDC data. Without a known labeler, it is difficult to trace the product\'s origin through the US regulatory supply chain.',
      },
      {
        name: 'labeler_country_matches_coo',
        displayName: 'Labeler Country Matches COO',
        hitEvidence: 'FDA records list manufacturer country as: {countries}. COO candidate is \'{coo}\'. This is a direct country match from FDA regulatory data, strongly supporting the stated COO.',
        missEvidence: 'No explicit manufacturer country data found in FDA records for this labeler, or the FDA-listed country does not match the stated COO. Without explicit country data from FDA, this factor cannot verify the labeler country.',
      },
      {
        name: 'product_marketing_status_active',
        displayName: 'Product Marketing Status Active',
        hitEvidence: 'Product has an active FDA marketing category: \'{marketing_category}\'. An active marketing status (NDA, ANDA, BLA, OTC) means the product has been approved through the US drug regulatory process, which requires disclosure of manufacturing locations and supports the credibility of the stated COO.',
        missEvidence: 'No active FDA marketing category found. Without an active marketing status, there is less regulatory evidence to support the product\'s stated Country of Origin.',
      },
    ],
  },
  {
    id: 'fda_establishments',
    name: 'FDA Establishments / DRLS',
    description: 'The FDA Drug Registration and Listing System (DRLS) tracks all registered manufacturing facilities. This source verifies whether the manufacturer has registered facilities and where they are located.',
    factorCount: 5,
    factors: [
      {
        name: 'has_registered_manufacturing_facility',
        displayName: 'Has Registered Manufacturing Facility',
        hitEvidence: 'FDA DRLS shows registered manufacturing facility: \'{firm_name}\' (FEI: {fei}), located in {country}. A registered facility with a known name and location confirms the manufacturer is known to the FDA and establishes where the product is physically manufactured.',
        missEvidence: 'No registered manufacturing facility found in FDA DRLS for this labeler. Without a facility registration, there is no FDA confirmation of where this product is manufactured.',
      },
      {
        name: 'has_fdf_manufacture_operation',
        displayName: 'Has FDF Manufacture Operation',
        hitEvidence: 'Facility \'{firm_name}\' (FEI: {fei}) in {country} is registered for Finished Dosage Form (FDF) manufacturing. FDF registration means the final drug product is manufactured at this specific facility, directly tying the product\'s physical production location to this country.',
        missEvidence: 'No facility is registered for FDF manufacturing. This means the final product may be manufactured elsewhere, weakening the link between any known facility\'s country and the product\'s actual COO.',
      },
      {
        name: 'facility_country_matches_coo',
        displayName: 'Facility Country Matches COO',
        hitEvidence: 'The registered manufacturing facility is located in \'{facility_country}\', which matches the stated COO of \'{coo}\'. This is strong evidence that the product is manufactured in the declared country.',
        missEvidence: 'Facility country is \'{facility_country}\', COO is \'{coo}\'. The facility country does not match the stated COO, which raises questions about the accuracy of the COO declaration.',
      },
      {
        name: 'registration_is_current',
        displayName: 'Registration Is Current',
        hitEvidence: 'The facility\'s FDA registration is current and active. An active registration means the facility is compliant with FDA inspection requirements and its manufacturing location data is up to date, supporting the reliability of the COO.',
        missEvidence: 'The facility registration is not current. An expired or inactive registration means the manufacturing location data may be outdated, reducing confidence in the stated COO.',
      },
      {
        name: 'single_country_manufacturing',
        displayName: 'Single Country Manufacturing',
        hitEvidence: 'All registered facilities for this labeler are in a single country. Single-country manufacturing simplifies COO determination -- the product is manufactured in one country, making the COO unambiguous.',
        missEvidence: 'Manufacturing occurs in multiple countries. Multi-country manufacturing complicates COO determination because different production stages may occur in different countries.',
      },
    ],
  },
  {
    id: 'fda_import_refusals',
    name: 'FDA Import Refusals',
    description: 'FDA Import Refusal data tracks products that have been refused entry into the United States. This source provides evidence about the product\'s international supply chain and whether it has been imported from foreign countries.',
    factorCount: 4,
    factors: [
      {
        name: 'import_refusal_found_for_product',
        displayName: 'Import Refusal Found for Product',
        hitEvidence: 'For US COO: No FDA import refusals found — consistent with domestic manufacturing (the product has no record of being refused entry as a foreign import). For non-US COO: Import refusal(s) found — confirms the product has been imported into the US from abroad, supporting the foreign origin claim.',
        missEvidence: 'For US COO: Import refusal(s) found — products were being imported FROM abroad INTO the US, which is evidence AGAINST a US Country of Origin. For non-US COO: No import refusals found — no US border evidence to confirm foreign origin.',
      },
      {
        name: 'import_refusal_found_for_labeler',
        displayName: 'Import Refusal Found for Labeler',
        hitEvidence: 'For US COO: No import refusals linked to this labeler — consistent with domestic manufacturing (no history of importing from abroad). For non-US COO: Import refusal(s) linked to this labeler — a history of importing supports the non-US origin claim.',
        missEvidence: 'For US COO: Import refusal(s) linked to this labeler — a history of importing products contradicts a US-origin claim. For non-US COO: No import refusals found — no US border import evidence to support the foreign origin claim.',
      },
      {
        name: 'refusal_country_matches_coo',
        displayName: 'Refusal Country Matches COO',
        hitEvidence: 'Import refusal country(ies): {countries}. COO: {coo}. The refusal country matches the stated COO, which corroborates that the product\'s supply chain involves the declared country of origin.',
        missEvidence: 'Import refusal country(ies) do not match the stated COO, or no country data available in import refusals. Cannot confirm supply chain alignment with declared COO.',
      },
      {
        name: 'refusal_charges_reference_manufacturing',
        displayName: 'Refusal Charges Reference Manufacturing',
        hitEvidence: 'Import refusal charge references manufacturing. Manufacturing-related charges in import refusals directly indicate foreign manufacturing activity, which is relevant evidence for COO determination.',
        missEvidence: 'No manufacturing-related charges found in import refusals. The refusal charges (if any) do not reference manufacturing practices.',
      },
    ],
  },
  {
    id: 'epa_tri',
    name: 'EPA Toxics Release Inventory (TRI)',
    description: 'The EPA Toxics Release Inventory tracks facilities that manufacture, process, or use significant amounts of toxic chemicals. Pharmaceutical manufacturing facilities are tracked here, providing evidence of domestic manufacturing infrastructure.',
    factorCount: 4,
    factors: [
      {
        name: 'tri_facility_found',
        displayName: 'TRI Facility Found',
        hitEvidence: '{count} EPA Toxics Release Inventory (TRI) facility(ies) found. TRI-reporting facilities are physical manufacturing/processing sites in the US. Their existence confirms domestic manufacturing infrastructure, supporting a US COO.',
        missEvidence: 'No EPA TRI facilities found for this labeler. The absence of TRI-reporting facilities means there is no EPA evidence of domestic chemical manufacturing or processing operations.',
      },
      {
        name: 'tri_has_pharma_naics',
        displayName: 'TRI Has Pharma NAICS',
        hitEvidence: 'TRI facilities have pharmaceutical NAICS codes (3254x = Pharmaceutical and Medicine Manufacturing). This confirms the labeler operates pharmaceutical manufacturing facilities, directly supporting the product\'s manufacturing origin.',
        missEvidence: 'No pharmaceutical NAICS codes (3254x) found in TRI data. The labeler\'s TRI-reported facilities are not classified as pharmaceutical manufacturing, so they may not be relevant to this product\'s manufacturing.',
      },
      {
        name: 'tri_location_matches_coo',
        displayName: 'TRI Location Matches COO',
        hitEvidence: 'TRI facility location(s) match the stated COO of \'{coo}\'. The TRI facility locations provide environmental regulatory evidence that manufacturing occurs in the declared country.',
        missEvidence: 'TRI facility locations do not match COO \'{coo}\', or no location data available from TRI.',
      },
      {
        name: 'tri_chemical_data_consistent',
        displayName: 'TRI Chemical Data Consistent',
        hitEvidence: 'TRI chemical release data is consistent with pharmaceutical manufacturing. Chemicals reported: {chemicals}. Pharma NAICS codes: {naics_codes}. Consistent chemical data confirms active pharmaceutical manufacturing operations at the reported facilities.',
        missEvidence: 'TRI chemical data is not consistent with pharmaceutical manufacturing. Chemicals reported: {chemicals}. No pharmaceutical NAICS codes (3254x) found among the TRI facilities.',
      },
    ],
  },
  {
    id: 'fda_shortages',
    name: 'FDA Drug Shortages',
    description: 'The FDA Drug Shortage Database tracks current and resolved drug shortages. Shortage records often contain information about manufacturing disruptions and their locations, which can reveal where a product is actually manufactured.',
    factorCount: 3,
    factors: [
      {
        name: 'shortage_record_found',
        displayName: 'Shortage Record Found',
        hitEvidence: '{count} FDA drug shortage record(s) found. Shortage records often contain information about manufacturing disruptions and their locations, which can reveal where a product is actually manufactured.',
        missEvidence: 'No FDA drug shortage records found. Without shortage data, there is no FDA-reported manufacturing disruption information to help verify the COO.',
      },
      {
        name: 'shortage_references_manufacturing_location',
        displayName: 'Shortage References Manufacturing Location',
        hitEvidence: 'Shortage reason references manufacturing. Mentions of manufacturing in shortage records reveal production locations and disruptions, providing direct evidence about where the product is made.',
        missEvidence: 'No manufacturing location referenced in shortage data. The shortage records (if any) don\'t mention specific manufacturing sites or locations.',
      },
      {
        name: 'shortage_country_matches_coo',
        displayName: 'Shortage Country Matches COO',
        hitEvidence: 'Shortage record country matches COO \'{coo}\'. Match confirms the manufacturing disruption is in the declared COO country.',
        missEvidence: 'Shortage record country does not match COO \'{coo}\', or no country data in shortage records. Cannot compare against stated COO.',
      },
    ],
  },
  {
    id: 'fda_recalls',
    name: 'FDA Recalls',
    description: 'FDA Recall data identifies products that have been recalled and the firms responsible. Recall records can identify the manufacturer and their country of operation, which directly supports COO verification.',
    factorCount: 4,
    factors: [
      {
        name: 'recall_records_found',
        displayName: 'Recall Records Found',
        hitEvidence: '{count} FDA recall(s) found. Recall records identify the recalling firm and often include manufacturing location details, which directly support COO verification.',
        missEvidence: 'No FDA recall records found. Without recall data, there is no recall-based evidence about the product\'s manufacturing origin.',
      },
      {
        name: 'recall_identifies_manufacturer',
        displayName: 'Recall Identifies Manufacturer',
        hitEvidence: 'Recalling firm(s) identified: {firms}. Identifying the manufacturer in recall data helps trace the product\'s origin and confirms which entity is responsible for production.',
        missEvidence: 'No manufacturer identified in recall records.',
      },
      {
        name: 'recall_country_matches_coo',
        displayName: 'Recall Country Matches COO',
        hitEvidence: 'Recall country matches COO \'{coo}\'. The recall firm\'s country matches the stated COO, confirming the product\'s manufacturing origin.',
        missEvidence: 'Recall country does not match COO \'{coo}\', or no country data in recall records.',
      },
      {
        name: 'recall_references_manufacturing_location',
        displayName: 'Recall References Manufacturing Location',
        hitEvidence: 'Recall reason references manufacturing. Manufacturing mentions in recall reasons provide direct evidence about where the product was made.',
        missEvidence: 'No manufacturing reference in recall reasons. The recall data (if any) does not mention specific manufacturing locations.',
      },
    ],
  },
  {
    id: 'dailymed_spl',
    name: 'DailyMed SPL (Structured Product Labeling)',
    description: 'DailyMed provides Structured Product Labeling (SPL) data submitted by manufacturers to the FDA. Drug labels can contain manufacturing information, country references, and manufacturer identification that support COO verification.',
    factorCount: 5,
    factors: [
      {
        name: 'spl_contains_manufacturing_info',
        displayName: 'SPL Contains Manufacturing Info',
        hitEvidence: 'DailyMed SPL contains manufacturing information (text: \'{manufacturing_text}\', manufacturer: \'{manufacturer}\', country: \'{country}\'). Manufacturing details in the drug label reveal where the product is formulated, packaged, or distributed, directly supporting COO determination.',
        missEvidence: 'No manufacturing information found in DailyMed SPL. The product label does not contain manufacturing details that could help verify the COO.',
      },
      {
        name: 'spl_country_identified',
        displayName: 'SPL Country Identified',
        hitEvidence: 'Country \'{country}\' identified in DailyMed SPL. A country reference in the drug label provides direct evidence about the product\'s manufacturing or distribution origin.',
        missEvidence: 'No country identified in the DailyMed SPL label text. Without country information in the label, this source cannot contribute to COO verification.',
      },
      {
        name: 'spl_country_matches_coo',
        displayName: 'SPL Country Matches COO',
        hitEvidence: 'SPL country \'{country}\' matches COO \'{coo}\'. The drug label\'s country reference directly corroborates the stated Country of Origin.',
        missEvidence: 'SPL country does not match COO \'{coo}\', or no country found in SPL to compare. Country mismatch between label and stated COO.',
      },
      {
        name: 'spl_manufacturer_name_identified',
        displayName: 'SPL Manufacturer Name Identified',
        hitEvidence: 'Manufacturer \'{manufacturer}\' identified in DailyMed SPL. Knowing the manufacturer from the drug label enables cross-referencing with FDA registrations and other sources to verify the COO.',
        missEvidence: 'No manufacturer name found in DailyMed SPL label.',
      },
      {
        name: 'spl_manufacturing_context_clear',
        displayName: 'SPL Manufacturing Context Clear',
        hitEvidence: 'The manufacturing context in the SPL label is clear and unambiguous (country: \'{country}\', manufacturer: \'{manufacturer}\'). Clear \'Manufactured in\' or \'Made in\' text in the drug label provides high-confidence evidence for COO.',
        missEvidence: 'Manufacturing context in the SPL label is unclear or ambiguous. Manufacturing info: \'{manufacturing_info}\'. Vague manufacturing references reduce confidence in using this source for COO verification.',
      },
    ],
  },
  {
    id: 'package_ocr',
    name: 'Package OCR / AI Vision',
    description: 'Physical product packaging is analyzed using OCR (Optical Character Recognition) and AI Vision to extract text from package images on DailyMed. Packaging often includes "Made in [Country]" or "Manufactured by [Company] in [Country]" text.',
    factorCount: 5,
    factors: [
      {
        name: 'package_image_found',
        displayName: 'Package Image Found',
        hitEvidence: 'Package image found on DailyMed. Product packaging often includes "Made in [Country]" or "Manufactured by [Company] in [Country]" text, which is direct physical evidence of the COO.',
        missEvidence: 'No package image found on DailyMed. Without a package image, OCR-based COO extraction cannot be performed.',
      },
      {
        name: 'package_text_extracted',
        displayName: 'Package Text Extracted',
        hitEvidence: 'Text extracted from package image. OCR text from physical packaging can reveal "Made in" labels, manufacturing addresses, and country markings that directly indicate COO.',
        missEvidence: 'No text could be extracted from the package image. OCR failed to read the packaging, so no text-based COO evidence is available from the physical product.',
      },
      {
        name: 'package_country_identified',
        displayName: 'Package Country Identified',
        hitEvidence: 'Country \'{country}\' identified from package text via AI analysis. A country printed on the product packaging is one of the strongest indicators of COO.',
        missEvidence: 'No country could be identified from the package image text. The packaging does not contain readable country-of-origin markings.',
      },
      {
        name: 'package_country_matches_coo',
        displayName: 'Package Country Matches COO',
        hitEvidence: 'Package country \'{country}\' matches COO \'{coo}\'. The physical packaging confirms the stated Country of Origin -- this is very strong evidence.',
        missEvidence: 'Package country does not match COO \'{coo}\', or no country found on package to compare. Package indicates a different country than the stated COO.',
      },
      {
        name: 'package_manufacturing_context_clear',
        displayName: 'Package Manufacturing Context Clear',
        hitEvidence: 'The manufacturing context on the package is clear (country: \'{country}\', text: \'{extracted_text}\'). Clear "Manufactured in" or "Made in" text on packaging provides definitive COO evidence.',
        missEvidence: 'Manufacturing context on the package is unclear or not present. Extracted text: \'{extracted_text}\'. No definitive "Made in" or "Manufactured in" statement was found.',
      },
    ],
  },
  {
    id: 'sec_edgar',
    name: 'SEC EDGAR 10-K Filings',
    description: 'SEC EDGAR provides public financial filings (10-K annual reports) that contain legally required, audited disclosures about manufacturing facilities, supply chain operations, and international operations. This is highly reliable COO evidence.',
    factorCount: 5,
    factors: [
      {
        name: 'sec_filing_found',
        displayName: 'SEC Filing Found',
        hitEvidence: 'SEC 10-K annual filing found. 10-K filings contain detailed disclosures about manufacturing facilities, supply chain operations, and international operations that reveal where products are actually made.',
        missEvidence: 'No SEC 10-K filing found. Without public financial disclosures, there is no SEC-based evidence about the company\'s manufacturing locations.',
      },
      {
        name: 'sec_manufacturing_section_found',
        displayName: 'SEC Manufacturing Section Found',
        hitEvidence: 'Manufacturing section found in 10-K filing (CIK: {cik}, key excerpt: \'{evidence_text}\'). The 10-K\'s manufacturing disclosures typically list facility locations, production capabilities, and supply chain details that directly indicate where products are manufactured.',
        missEvidence: 'No manufacturing section found in the 10-K filing (CIK: {cik}). The company\'s SEC filing does not contain identifiable manufacturing disclosures.',
      },
      {
        name: 'sec_country_identified',
        displayName: 'SEC Country Identified',
        hitEvidence: 'Country \'{country}\' identified from 10-K manufacturing disclosures. SEC filings provide legally required, audited disclosures about manufacturing locations -- this is highly reliable COO evidence.',
        missEvidence: 'No country identified from 10-K manufacturing disclosures. The SEC filing does not reveal specific manufacturing countries.',
      },
      {
        name: 'sec_country_matches_coo',
        displayName: 'SEC Country Matches COO',
        hitEvidence: 'SEC filing country \'{country}\' matches COO \'{coo}\'. The company\'s legally required SEC disclosure confirms manufacturing in the declared COO country -- this is strong, auditable evidence.',
        missEvidence: 'SEC country does not match COO \'{coo}\', or no country data in SEC filing. SEC disclosure indicates a different manufacturing country than stated COO, or no data to compare.',
      },
      {
        name: 'sec_manufacturing_details_clear',
        displayName: 'SEC Manufacturing Details Clear',
        hitEvidence: 'Manufacturing details in the 10-K are clear and specific (US manufacturing: {us_manufacture}, Foreign manufacturing: {foreign_manufacture}, AI confidence: {confidence}, evidence: \'{evidence_text}\'). Detailed, unambiguous manufacturing disclosures in SEC filings provide high-confidence COO evidence.',
        missEvidence: 'Manufacturing details in the 10-K are unclear or absent (AI confidence: {confidence}, evidence: \'{evidence_text}\'). Without clear manufacturing disclosures, the SEC filing provides limited COO evidence.',
      },
    ],
  },
];

// ============================================================
// PNS FACTOR DEFINITIONS (5 Data Sources / Categories)
// ============================================================

const PNS_DATA_SOURCES: DataSourceGroup[] = [
  {
    id: 'political_donations',
    name: 'Political Donations',
    description: 'Executive political donations made by individuals employed at or associated with the manufacturer. Data comes from the executive_political_donations table, linked through the manufacturer_executives table.',
    factorCount: 1,
    factors: [
      {
        name: 'political_donations',
        displayName: 'Executive Political Donations Found',
        hitEvidence: 'One or more executive political donation records found for this manufacturer. Executives at this company have made personal political contributions to candidates, parties, or committees. The evidence includes donor names, recipients, amounts, and dates.',
        missEvidence: 'No executive political donation records found for this manufacturer. No records exist in the executive_political_donations table linked to this manufacturer\'s executives.',
      },
    ],
  },
  {
    id: 'pac_contributions',
    name: 'PAC Contributions',
    description: 'Political Action Committee (PAC) contributions and disbursements linked to the manufacturer. Data comes from the pac_contributions table, tracking committee disbursements to candidates and other political entities.',
    factorCount: 1,
    factors: [
      {
        name: 'pac_contributions',
        displayName: 'PAC Contributions Found',
        hitEvidence: 'One or more PAC contribution records found for this manufacturer. The manufacturer\'s PAC has made political disbursements to candidates, parties, or committees. Evidence includes committee names, recipients, amounts, and election cycles.',
        missEvidence: 'No PAC contribution records found for this manufacturer. No records exist in the pac_contributions table for this manufacturer.',
      },
    ],
  },
  {
    id: 'lobbying_filings',
    name: 'Lobbying Filings',
    description: 'Federal lobbying filings and registrations linked to the manufacturer. Data comes from the manufacturer_lobbying_filings table, tracking lobbying activity reported to the Senate Office of Public Records.',
    factorCount: 1,
    factors: [
      {
        name: 'lobbying_filings',
        displayName: 'Lobbying Filings Found',
        hitEvidence: 'One or more lobbying filing records found for this manufacturer. The manufacturer has registered federal lobbying activity, including registrant firms, client relationships, filing years, and reported amounts.',
        missEvidence: 'No lobbying filing records found for this manufacturer. No records exist in the manufacturer_lobbying_filings table for this manufacturer.',
      },
    ],
  },
  {
    id: 'stock_holdings',
    name: 'Politician Stock Holdings',
    description: 'Stock holdings and transactions by U.S. politicians in the manufacturer\'s publicly traded stock. Data comes from the politician_stock_holdings table, tracking congressional financial disclosures.',
    factorCount: 1,
    factors: [
      {
        name: 'stock_holdings',
        displayName: 'Politician Stock Holdings Found',
        hitEvidence: 'One or more politician stock holding or transaction records found for this manufacturer. U.S. politicians hold or actively trade stock in this manufacturer. Evidence includes politician names, parties, chambers, transaction types, and amounts.',
        missEvidence: 'No politician stock holding records found for this manufacturer. No records exist in the politician_stock_holdings table for this manufacturer.',
      },
    ],
  },
  {
    id: 'ccp_connections',
    name: 'CCP Connections',
    description: 'Connections to the Chinese Communist Party (CCP) or Chinese state entities. Data comes from the ccp_matches table (entity matching against CCP-related lists) and the manufacturers table ccp_flag field (manual or geo-based flagging).',
    factorCount: 1,
    factors: [
      {
        name: 'ccp_connections',
        displayName: 'CCP Connections Found',
        hitEvidence: 'One or more CCP connection indicators found for this manufacturer. This includes matches in the ccp_matches table (entity matching against CCP-affiliated lists) and/or the ccp_flag being set on the manufacturer record. Evidence includes match types, list sources, and matched entity names.',
        missEvidence: 'No CCP connections found for this manufacturer. No records exist in the ccp_matches table and no ccp_flag is set on the manufacturer record.',
      },
    ],
  },
];

// Disabled factors for PNS (none currently)
const PNS_DISABLED_FACTORS: Record<string, string> = {};

// ============================================================
// QRS: ALL 24 FACTOR DEFINITIONS (4 Data Sources)
// ============================================================

const QRS_DATA_SOURCES: DataSourceGroup[] = [
  {
    id: 'fda_recalls',
    name: 'FDA Recalls / Enforcement',
    description: 'The FDA Drug Recall / Enforcement database tracks products recalled from the market due to safety or quality issues. Recall classification (Class I/II/III), recency, and cGMP-related reasons are evaluated as quality signals.',
    factorCount: 7,
    factors: [
      {
        name: 'class_i_recall_found',
        displayName: 'Class I Recall Found (Most Serious)',
        hitEvidence: 'A Class I recall (most serious — reasonable probability of serious adverse health consequences or death) was found. This is a significant negative quality signal indicating the product posed a serious health risk.',
        missEvidence: 'No Class I recall found. The absence of the most serious recall classification is a positive quality indicator.',
      },
      {
        name: 'class_ii_recall_found',
        displayName: 'Class II Recall Found',
        hitEvidence: 'A Class II recall (may cause temporary or medically reversible adverse health consequences) was found. This indicates a moderate quality concern with the product.',
        missEvidence: 'No Class II recall found. The product has no history of recalls for temporary or medically reversible adverse health consequences.',
      },
      {
        name: 'class_iii_recall_found',
        displayName: 'Class III Recall Found',
        hitEvidence: 'A Class III recall (unlikely to cause adverse health consequences) was found. While less serious, any recall indicates a quality control issue.',
        missEvidence: 'No Class III recall found. The product has no history of recalls even for minor issues.',
      },
      {
        name: 'recall_within_2_years',
        displayName: 'Recall Within 2 Years',
        hitEvidence: 'A recall was initiated within the last 2 years. Recent recalls indicate current or recent manufacturing issues and are a stronger negative quality signal.',
        missEvidence: 'No recalls within the last 2 years. If recalls exist, they are older and may reflect issues that have since been resolved.',
      },
      {
        name: 'multiple_recalls_found',
        displayName: 'Multiple Recalls Found (>= 2)',
        hitEvidence: 'Multiple recall records found (threshold: >= 2). Multiple recalls suggest a pattern of quality issues rather than an isolated incident.',
        missEvidence: 'Fewer than 2 recall records found. The low number does not suggest a pattern of recurring quality issues.',
      },
      {
        name: 'recall_reason_is_cgmp',
        displayName: 'Recall Reason References cGMP Violation',
        hitEvidence: 'Recall reason references cGMP (Current Good Manufacturing Practice) violation. cGMP-related recalls indicate systematic manufacturing quality failures.',
        missEvidence: 'No recall reasons reference cGMP violations. If recalls exist, they are not related to systematic manufacturing practice failures.',
      },
      {
        name: 'recall_is_ongoing',
        displayName: 'Recall Status Is Ongoing',
        hitEvidence: 'An active/ongoing recall exists. Ongoing recalls represent current quality concerns that have not yet been fully resolved.',
        missEvidence: 'No ongoing recalls. All recalls (if any) have been terminated or completed, suggesting issues have been addressed.',
      },
    ],
  },
  {
    id: 'fda_faers',
    name: 'FDA FAERS Adverse Events',
    description: 'The FDA Adverse Event Reporting System (FAERS) tracks voluntary reports of adverse drug events. High volumes of adverse events, death or hospitalization outcomes, and safety signals are negative quality indicators.',
    factorCount: 7,
    factors: [
      {
        name: 'serious_adverse_event_reported',
        displayName: 'Serious Adverse Event Reported',
        hitEvidence: 'Adverse event report(s) found in FDA FAERS database. The presence of adverse event reports indicates that safety concerns have been reported for this product.',
        missEvidence: 'No adverse event reports found in FDA FAERS database. The absence of reported adverse events is a positive quality indicator.',
      },
      {
        name: 'death_outcome_reported',
        displayName: 'Death Outcome Reported',
        hitEvidence: 'Death outcome reported in FAERS data. A death outcome associated with the product is the most severe adverse event and represents a critical negative quality signal.',
        missEvidence: 'No death outcomes reported in FAERS data. The absence of death outcomes is a positive safety indicator.',
      },
      {
        name: 'hospitalization_outcome_reported',
        displayName: 'Hospitalization Outcome Reported',
        hitEvidence: 'Hospitalization outcome reported in FAERS data. Hospitalizations linked to the product indicate serious adverse reactions requiring medical intervention.',
        missEvidence: 'No hospitalization outcomes reported in FAERS data. The absence of hospitalization-related adverse events is a positive indicator.',
      },
      {
        name: 'high_adverse_event_count',
        displayName: 'High Adverse Event Count (>= 100)',
        hitEvidence: 'High volume of adverse event reports found (threshold: >= 100). A high volume suggests widespread quality or safety concerns.',
        missEvidence: 'Adverse event count is below the high-volume threshold (< 100).',
      },
      {
        name: 'adverse_event_within_1_year',
        displayName: 'Adverse Event Within 1 Year',
        hitEvidence: 'Recent adverse events detected (within approximately 1 year). Recent adverse event activity indicates ongoing quality or safety concerns.',
        missEvidence: 'No recent adverse events detected. The product\'s safety profile has been stable.',
      },
      {
        name: 'safety_signal_detected',
        displayName: 'Safety Signal Detected (>= 50 Reports)',
        hitEvidence: 'Volume of adverse event reports exceeds the safety signal threshold (>= 50), indicating a statistically notable pattern of adverse events.',
        missEvidence: 'Report volume is below the safety signal threshold (< 50).',
      },
      {
        name: 'multiple_event_types_reported',
        displayName: 'Multiple Event Types Reported (>= 2)',
        hitEvidence: 'Multiple different event types/categories reported in FAERS (threshold: >= 2). Multiple event types suggest diverse adverse reactions.',
        missEvidence: 'Limited event type diversity (< 2 types). Does not suggest broad quality concerns.',
      },
    ],
  },
  {
    id: 'fda_shortages',
    name: 'FDA Drug Shortages',
    description: 'The FDA Drug Shortage Database tracks current and resolved drug shortages. Shortages can indicate manufacturing quality issues, capacity problems, or supply chain vulnerabilities that affect product quality and availability.',
    factorCount: 5,
    factors: [
      {
        name: 'currently_in_shortage',
        displayName: 'Currently In Shortage',
        hitEvidence: 'The product is currently in a drug shortage, which may indicate manufacturing or supply chain quality issues affecting availability.',
        missEvidence: 'No shortage records found. The product is not currently in shortage, suggesting stable manufacturing and supply chain quality.',
      },
      {
        name: 'shortage_is_critical',
        displayName: 'Shortage Is Critical',
        hitEvidence: 'Shortage status is "current" (critical). A critical/current shortage status indicates an active supply disruption.',
        missEvidence: 'Shortage status is not critical/current. If shortage records exist, they may be resolved or in a less urgent status.',
      },
      {
        name: 'shortage_duration_over_6_months',
        displayName: 'Shortage Duration Over 6 Months',
        hitEvidence: 'Shortage has been ongoing for more than 6 months. Prolonged shortages suggest persistent manufacturing quality or capacity issues.',
        missEvidence: 'Shortage duration does not exceed 6 months, or no duration data available.',
      },
      {
        name: 'shortage_reason_is_manufacturing',
        displayName: 'Shortage Reason Is Manufacturing',
        hitEvidence: 'Shortage reason references manufacturing. Manufacturing-related shortages directly indicate production quality or capacity problems.',
        missEvidence: 'Shortage reason does not reference manufacturing. If a shortage exists, it may be due to demand or distribution factors.',
      },
      {
        name: 'no_alternative_supplier',
        displayName: 'No Alternative Supplier',
        hitEvidence: 'No alternative supplier available. Single-source products pose higher supply chain risk and may indicate limited quality oversight.',
        missEvidence: 'Alternative suppliers are available or supply data is inconclusive. Multiple suppliers generally provide better quality assurance.',
      },
    ],
  },
  {
    id: 'fda_inspections',
    name: 'FDA Inspections / 483s',
    description: 'FDA facility inspections and Form 483 observations track manufacturing compliance. Inspection findings, warning letters, and cGMP violations are direct indicators of manufacturing quality. Note: This data source is not yet connected to a live API — all 5 factors are currently disabled.',
    factorCount: 5,
    factors: [
      {
        name: 'form_483_issued',
        displayName: 'Form 483 Issued',
        hitEvidence: 'Form 483 inspection observation(s) found. FDA Form 483 documents observed conditions that may violate the Food, Drug, and Cosmetic Act.',
        missEvidence: 'No Form 483 issued. The absence of inspection observations is a positive quality indicator.',
      },
      {
        name: 'warning_letter_issued',
        displayName: 'Warning Letter Issued',
        hitEvidence: 'FDA Warning Letter issued to the manufacturer. Warning letters indicate significant regulatory violations.',
        missEvidence: 'No FDA Warning Letter issued. The manufacturer maintains acceptable compliance.',
      },
      {
        name: 'inspection_within_2_years',
        displayName: 'Inspection Within 2 Years',
        hitEvidence: 'FDA inspection conducted within the last 2 years. Recent inspections provide current quality assurance data.',
        missEvidence: 'No FDA inspection within the last 2 years, or no inspection data available.',
      },
      {
        name: 'multiple_observations',
        displayName: 'Multiple Observations',
        hitEvidence: 'Multiple inspection observations found, indicating systemic quality issues at the facility.',
        missEvidence: 'No multiple observations, or no inspection data available.',
      },
      {
        name: 'cgmp_violation_found',
        displayName: 'cGMP Violation Found',
        hitEvidence: 'cGMP violation found during inspection. The manufacturer does not meet FDA quality standards for drug production.',
        missEvidence: 'No cGMP violations found. The manufacturer appears to meet FDA quality standards.',
      },
    ],
  },
];

// ============================================================
// COMPONENT
// ============================================================

export default function EvidenceDefinitions() {
  const { agentName } = useParams<{ agentName: string }>();
  const navigate = useNavigate();
  const { T } = useTheme();
  const { isMobile } = useResponsive();

  const displayName = AGENT_DISPLAY[agentName ?? ''] ?? agentName ?? 'Agent';
  const isPns = agentName === 'pns';
  const isQrs = agentName === 'qrs';
  const activeSources = isPns ? PNS_DATA_SOURCES : isQrs ? QRS_DATA_SOURCES : COO_DATA_SOURCES;
  const activeDisabled = isPns ? PNS_DISABLED_FACTORS : isQrs ? QRS_DISABLED_FACTORS : COO_DISABLED_FACTORS;
  const allFactors = activeSources.reduce((sum, ds) => sum + ds.factors.length, 0);
  const totalEnabled = activeSources.reduce((sum, ds) => sum + ds.factors.filter(f => !activeDisabled[f.name]).length, 0);
  const totalDisabled = allFactors - totalEnabled;
  const totalFactors = totalEnabled;

  // Track expanded sources and factors
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set(activeSources.map(ds => ds.id)));
  const [expandedFactors, setExpandedFactors] = useState<Set<string>>(new Set());

  const toggleSource = (id: string) => {
    setExpandedSources(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleFactor = (name: string) => {
    setExpandedFactors(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedSources(new Set(activeSources.map(ds => ds.id)));
    setExpandedFactors(new Set(activeSources.flatMap(ds => ds.factors.map(f => f.name))));
  };

  const collapseAll = () => {
    setExpandedSources(new Set());
    setExpandedFactors(new Set());
  };

  return (
    <div style={{ padding: isMobile ? 16 : 32, maxWidth: 1100, margin: '0 auto' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate('/agent-activity')}
          style={{ background: 'none', border: 'none', color: T.textLight, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', padding: 0 }}
        >
          Agent Activity
        </button>
        <span style={{ color: T.textLight, fontSize: 13 }}>/</span>
        <button
          onClick={() => navigate(`/agent-detail/${agentName}`)}
          style={{ background: 'none', border: 'none', color: T.textLight, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', padding: 0 }}
        >
          {displayName}
        </button>
        <span style={{ color: T.textLight, fontSize: 13 }}>/</span>
        <span style={{ color: T.textDark, fontSize: 13, fontWeight: 600 }}>Evidence Definitions</span>
      </div>

      {/* Page Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${T.navy}15, ${T.gold}15)`,
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.navy} strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </div>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: T.textDark, margin: 0 }}>Evidence Definitions</h1>
            <div style={{ fontSize: 13, color: T.textLight, marginTop: 2 }}>
              {displayName} &middot; {totalEnabled} Active Factors across {activeSources.length} Data Sources{totalDisabled > 0 ? ` (${totalDisabled} disabled)` : ''}
            </div>
          </div>
        </div>

        {/* Description */}
        <div style={{
          background: `${T.navy}08`, border: `1px solid ${T.navy}20`, borderRadius: T.radius,
          padding: 16, marginTop: 16, fontSize: 13, lineHeight: 1.6, color: T.textDark,
        }}>
          The COO Scoring Agent evaluates all {allFactors} factors and has determined that {totalDisabled} are existence/status
          checks that cannot confirm or validate a specific country of origin. These are <strong>disabled</strong> from scoring (shown grayed out below).
          The remaining <strong>{totalEnabled} enabled factors</strong> are the ones the agent uses for COO validation.
          Each factor is evaluated as a <strong>HIT</strong> (evidence supports COO) or <strong>MISS</strong> (evidence does not support COO).
          Every enabled factor carries equal weight: <strong>{(100 / totalFactors).toFixed(2)}%</strong> ({`100% / ${totalFactors} enabled factors`}).
          The total COO confidence score is the sum of all hitting enabled factors' weights.
          When a data source returns no data for a product, the factor evaluates as MISS — it is not disabled.
        </div>

        {/* Expand/Collapse controls */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
          <button
            onClick={expandAll}
            style={{
              padding: '6px 14px', borderRadius: 6, border: `1px solid ${T.border}`,
              background: T.white, color: T.textDark, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            style={{
              padding: '6px 14px', borderRadius: 6, border: `1px solid ${T.border}`,
              background: T.white, color: T.textDark, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Data Source Sections */}
      {activeSources.map((source, srcIdx) => {
        const isSourceExpanded = expandedSources.has(source.id);

        return (
          <div key={source.id} style={{
            marginBottom: 20, borderRadius: T.radius,
            border: `1px solid ${T.border}`, background: T.white,
            boxShadow: T.shadow, overflow: 'hidden',
          }}>
            {/* Source Header */}
            <button
              onClick={() => toggleSource(source.id)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 20px', background: `${T.navy}06`, border: 'none',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `${T.navy}12`, color: T.navy, fontSize: 14, fontWeight: 800,
                }}>
                  {srcIdx + 1}
                </div>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.textDark }}>{source.name}</div>
                  <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>
                    {(() => {
                      const enabledCount = source.factors.filter(f => !activeDisabled[f.name]).length;
                      const disabledCount = source.factors.length - enabledCount;
                      return `${enabledCount} active factor${enabledCount !== 1 ? 's' : ''}${disabledCount > 0 ? ` (${disabledCount} disabled)` : ''} \u00B7 ${(enabledCount * 100 / totalFactors).toFixed(1)}% of total weight`;
                    })()}
                  </div>
                </div>
              </div>
              <svg
                width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke={T.textLight} strokeWidth="2"
                style={{ transform: isSourceExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {isSourceExpanded && (
              <div style={{ padding: '0 20px 16px' }}>
                {/* Source description */}
                <div style={{
                  fontSize: 12, color: T.textLight, lineHeight: 1.5,
                  padding: '12px 0', borderBottom: `1px solid ${T.border}`,
                  marginBottom: 12,
                }}>
                  {source.description}
                </div>

                {/* Factors */}
                {source.factors.map((factor, fIdx) => {
                  const isFactorExpanded = expandedFactors.has(factor.name);
                  const factorNumber = activeSources.slice(0, srcIdx).reduce((sum, s) => sum + s.factors.length, 0) + fIdx + 1;
                  const isDisabled = !!activeDisabled[factor.name];
                  const disabledReason = activeDisabled[factor.name] || '';

                  return (
                    <div key={factor.name} style={{
                      marginBottom: fIdx < source.factors.length - 1 ? 10 : 0,
                      borderRadius: 8, border: `1px solid ${isDisabled ? `${T.textLight}30` : (T.borderLight || T.border)}`,
                      overflow: 'hidden',
                      opacity: isDisabled ? 0.6 : 1,
                    }}>
                      {/* Factor header */}
                      <button
                        onClick={() => toggleFactor(factor.name)}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '12px 16px', background: isDisabled ? `${T.textLight}06` : isFactorExpanded ? `${T.navy}04` : 'transparent',
                          border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700, color: T.textLight,
                            minWidth: 28, textAlign: 'center',
                          }}>
                            #{factorNumber}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: isDisabled ? T.textLight : T.textDark, textAlign: 'left' }}>
                            {factor.displayName}
                          </span>
                          {isDisabled && (
                            <span style={{
                              fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                              background: `${T.textLight}15`, color: T.textLight,
                              textTransform: 'uppercase' as const, letterSpacing: '0.05em',
                            }}>
                              Disabled
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, color: T.textLight,
                            background: isDisabled ? `${T.textLight}08` : `${T.navy}08`, padding: '2px 8px', borderRadius: 4,
                          }}>
                            {isDisabled ? 'N/A' : `~${(100 / totalFactors).toFixed(2)}%`}
                          </span>
                          <svg
                            width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke={T.textLight} strokeWidth="2"
                            style={{ transform: isFactorExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                          >
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </div>
                      </button>

                      {/* Factor evidence definitions */}
                      {isFactorExpanded && (
                        <div style={{ padding: '0 16px 16px' }}>
                          {/* Disabled reason banner */}
                          {isDisabled && (
                            <div style={{
                              marginTop: 8, marginBottom: 10, borderRadius: 8, overflow: 'hidden',
                              border: `1px solid ${T.textLight}30`, background: `${T.textLight}06`,
                              padding: '12px 14px',
                            }}>
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6,
                              }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.textLight} strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" />
                                  <line x1="12" y1="8" x2="12" y2="12" />
                                  <line x1="12" y1="16" x2="12.01" y2="16" />
                                </svg>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: T.textLight }}>
                                    Disabled — {isPns ? 'Cannot Assess Political Nexus' : 'Cannot Confirm COO'}
                                  </span>
                              </div>
                              <div style={{ fontSize: 12, lineHeight: 1.6, color: T.textLight }}>
                                {disabledReason}
                              </div>
                              <div style={{ fontSize: 11, color: T.textLight, marginTop: 8, fontStyle: 'italic', opacity: 0.8 }}>
                                This factor is still evaluated for transparency but its result does not contribute to the {isPns ? 'PNS' : 'COO confidence'} score.
                              </div>
                            </div>
                          )}
                          {/* HIT Evidence */}
                          <div style={{
                            marginTop: 8, borderRadius: 8, overflow: 'hidden',
                            border: `1px solid #22C55E40`,
                          }}>
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '8px 12px', background: '#22C55E10',
                              borderBottom: `1px solid #22C55E20`,
                            }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5">
                                <path d="M9 12l2 2 4-4" />
                                <circle cx="12" cy="12" r="10" />
                              </svg>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#22C55E' }}>
                                HIT -- {isPns ? 'Political Nexus Evidence Found' : 'COO Evidence Supports'}
                              </span>
                            </div>
                            <div style={{
                              padding: '12px 14px', fontSize: 13, lineHeight: 1.6, color: T.textDark,
                              background: '#22C55E04',
                            }}>
                              {factor.hitEvidence}
                            </div>
                          </div>

                          {/* MISS Evidence */}
                          <div style={{
                            marginTop: 10, borderRadius: 8, overflow: 'hidden',
                            border: `1px solid #EF444440`,
                          }}>
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '8px 12px', background: '#EF444410',
                              borderBottom: `1px solid #EF444420`,
                            }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2.5">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="15" y1="9" x2="9" y2="15" />
                                <line x1="9" y1="9" x2="15" y2="15" />
                              </svg>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#EF4444' }}>
                                MISS -- {isPns ? 'No Political Nexus Evidence' : 'COO Evidence Does Not Support'}
                              </span>
                            </div>
                            <div style={{
                              padding: '12px 14px', fontSize: 13, lineHeight: 1.6, color: T.textDark,
                              background: '#EF444404',
                            }}>
                              {factor.missEvidence}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Footer note */}
      <div style={{
        marginTop: 24, padding: 16, borderRadius: T.radius,
        background: `${T.navy}06`, border: `1px solid ${T.navy}15`,
        fontSize: 12, color: T.textLight, lineHeight: 1.6,
      }}>
        <strong>Note:</strong> {isPns ? (
          <>Each of the 5 political categories is evaluated as a binary HIT or MISS based on whether any records exist in the corresponding database table for the product's manufacturer. Each enabled category contributes 20% to the total PNS score (0-100%). A score of 0% means no political connections were found; 100% means all 5 categories had evidence.</>
        ) : isQrs ? (
          <>
            The QRS agent uses a <strong>deductive</strong> scoring model — it starts at 100% and subtracts for each
            negative quality signal (hit) found. Only the {totalFactors} enabled factors contribute to the score.
            The {totalDisabled} disabled factors (FDA Inspections/483s) are shown grayed out because the data source
            is not yet connected to a live API. When a data source returns no data for a product, the factor evaluates
            as MISS (not disabled).
          </>
        ) : (
          <>
            Values shown in curly braces (e.g., {'{ndc}'}, {'{coo}'}, {'{country}'}, {'{labeler}'})
            are dynamic placeholders that are replaced with actual data at runtime when a product is scored.
            The evidence text above represents the template — actual evidence on the Products Scored page will contain
            real values from the scoring run. When a data source returns no data for a product, the factor evaluates
            as MISS (not disabled) — disabled factors are only the {totalDisabled} existence/status checks that can never confirm COO.
          </>
        )}
      </div>
    </div>
  );
}
